/**
 * ACC-1 · Identidad local del jugador: **nick persistente + UUID de invitado**,
 * sin login ni servidor de cuentas.
 *
 * Dos datos, con propósitos distintos:
 *  - `playerName` — lo que ven los demás. Lo elige el jugador y se puede cambiar.
 *  - `guestId` — UUID anónimo generado en el dispositivo la primera vez y
 *    guardado en `localStorage`. NO es un login: no prueba quién eres, no viaja
 *    con datos personales y se puede borrar limpiando el navegador. Sirve para
 *    reconocer al mismo navegador entre partidas (reconexión, telemetría de
 *    errores, y mañana el *account linking* de M2: la cuenta real se linkea a
 *    este id y el progreso de invitado no se pierde).
 *
 * El filtro del nombre (SEC-2) es el MISMO módulo que usa el servidor
 * (`@tak/sim/moderation`): aquí solo adelanta el veredicto para que el jugador
 * lo vea al teclear; la última palabra siempre la tiene el servidor.
 */
import { MAX_NAME, moderateName, type NameVerdict } from "@tak/sim";

export { MAX_NAME };
export type { NameVerdict };

const NAME_KEY = "tak.playerName";
const GUEST_KEY = "tak.guestId";

/**
 * Almacenamiento inyectable. En el navegador es `localStorage`; en los tests
 * (y en un WebView con almacenamiento bloqueado) se sustituye sin tocar la
 * lógica. Todas las escrituras son tolerantes a fallo: modo incógnito, cuota
 * llena o cookies bloqueadas NO deben impedir jugar.
 */
export interface IdentityStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStore(): IdentityStore | null {
  try {
    // El acceso en sí puede lanzar (iframes con cookies de terceros bloqueadas).
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

let store: IdentityStore | null = defaultStore();

/** Sustituye el almacenamiento (tests / entornos sin `localStorage`). */
export function setIdentityStore(s: IdentityStore | null): void {
  store = s;
}

function read(key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    store?.setItem(key, value);
  } catch {
    /* almacenamiento no disponible: seguimos solo en memoria */
  }
}

/** Recorta y limpia un nombre a lo permitido. Mismo criterio que el servidor. */
export function sanitizeName(name: string): string {
  return moderateName(name).name;
}

/** Veredicto completo del nombre (para avisar al jugador de por qué cambió). */
export function checkName(name: string): NameVerdict {
  return moderateName(name);
}

export function loadPlayerName(): string | null {
  const v = read(NAME_KEY);
  if (!v) return null;
  const s = sanitizeName(v);
  return s || null;
}

export function savePlayerName(name: string): void {
  write(NAME_KEY, sanitizeName(name));
}

/** UUID v4 con el `crypto` del navegador, o un fallback si no está disponible. */
function newUuid(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  if (c && typeof c.getRandomValues === "function") {
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40; // versión 4
    b[8] = (b[8] & 0x3f) | 0x80; // variante RFC 4122
    const hex = [...b].map((n) => n.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Último recurso (navegador muy viejo o contexto no seguro): no es
  // criptográfico, pero este id no protege nada — solo distingue navegadores.
  return `x-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** ¿Tiene forma de id de invitado guardado por nosotros? (defensa ante basura). */
function isValidGuestId(v: string): boolean {
  return /^[a-zA-Z0-9-]{8,64}$/.test(v);
}

/**
 * Id de invitado de este dispositivo. Se crea en la primera llamada y se
 * reutiliza siempre; si el almacenamiento no está disponible, devuelve uno
 * efímero (la partida funciona igual, solo no persiste entre recargas).
 */
export function getGuestId(): string {
  const saved = read(GUEST_KEY);
  if (saved && isValidGuestId(saved)) return saved;
  const fresh = newUuid();
  write(GUEST_KEY, fresh);
  return fresh;
}

/**
 * Id de esta PESTAÑA. Distinto del de invitado, y por una razón concreta.
 *
 * El de invitado identifica el DISPOSITIVO (vive en `localStorage`, compartido
 * por todas las ventanas del navegador). El servidor lo usaba para reconocer a
 * quien vuelve tras una caída y devolverle su sitio… y con dos ventanas abiertas
 * eso resultaba catastrófico: para el servidor eran el mismo dispositivo, así
 * que cada ventana parecía el fantasma de la otra y se desahuciaban en bucle —
 * medido, 40 desconexiones en 12 segundos, alternándose.
 *
 * Una pestaña tiene UNA conexión, y volver a conectar desde ella sí es "yo otra
 * vez". Eso es exactamente `sessionStorage`: propio de cada pestaña y superviva
 * a las recargas. Abrir una ventana nueva da un id nuevo, que es lo correcto:
 * es otro participante, no el mismo volviendo.
 */
const SESSION_KEY = "tak.sessionId";
let ephemeralSessionId: string | null = null;

function sessionStore(): IdentityStore | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

export function getSessionId(): string {
  const s = sessionStore();
  try {
    const saved = s?.getItem(SESSION_KEY);
    if (saved && isValidGuestId(saved)) return saved;
  } catch { /* almacenamiento bloqueado: se cae al efímero */ }
  const fresh = ephemeralSessionId ?? newUuid();
  ephemeralSessionId = fresh;
  try { s?.setItem(SESSION_KEY, fresh); } catch { /* da igual: ya vale en memoria */ }
  return fresh;
}

/** Identidad completa del jugador local (lo que se manda al conectar). */
export interface LocalIdentity {
  guestId: string;
  name: string | null;
  /** true mientras no haya cuenta real linkeada (hoy: siempre). */
  isGuest: boolean;
}

export function loadIdentity(): LocalIdentity {
  return { guestId: getGuestId(), name: loadPlayerName(), isGuest: true };
}
